import type { WorkViewKey } from '../../../types/encounterWork';
import type { ColumnDef } from '../shared';

/**
 * The Claims tab's column sets, one per kind of view (GDN-3381, GDN-3383,
 * GDN-4023). Pure: the tab picks a set by the rail view it is showing and
 * hands it to the column preferences, which then own what is visible.
 */

export const getDefaultColumns = (subjectLabel: string): ColumnDef[] => [
  { key: 'service_date', label: 'Date', required: true, width: '82px' },
  { key: 'patient_name', label: subjectLabel, required: true, type: 'patient', width: '1.1fr' },
  { key: 'provider_name', label: 'Provider', width: '1.2fr' },
  { key: 'payer_name', label: 'Payer', width: '0.9fr' },
  { key: 'issues', label: 'Issues and findings', required: true, width: '1.8fr' },
  { key: 'amount', label: 'Charge', required: true, type: 'mono', align: 'right', width: '90px' },
  { key: 'work_state', label: 'State', required: true, width: '132px' },
];

/**
 * Claims to fix asks a different question than the rest of the list. The
 * biller is not deciding whether to send a claim; the payer already answered,
 * and the work is reading that answer. So the pre-flight advice column
 * (`issues`) goes, and what the payer said arrives in its place: the outcome,
 * the reason codes over the category they normalize to, how long the claim
 * has been sitting on that answer, and the claim number a payer phone call
 * needs (GDN-3381).
 */
/* GDN-3383 added a tenth column, so the fixed widths went on a diet: the
   columns holding one short token (Claim #, Outcome, Charge, Days) each gave
   up a few pixels they were not using, and the two columns that carry
   SENTENCES — Reason and Payer claim # — took the room. A number never
   truncates; a caption does, and a truncated caption is the one that costs
   a biller a phone call. */
export const getFixColumns = (subjectLabel: string): ColumnDef[] => [
  { key: 'service_date', label: 'Date', required: true, width: '82px' },
  { key: 'patient_name', label: subjectLabel, required: true, type: 'patient', width: '1.3fr' },
  /* No `type: 'mono'`: the cell renderer owns this column, because only a
     REAL claim number may wear the mono treatment. */
  { key: 'claim_number', label: 'Claim #', width: '88px' },
  /* No Provider here, unlike every other view. Nobody calls the rendering
     clinician about a denial; the payer, the codes and the days are the
     conversation, and on a table this wide the column that earns its place
     last is the one that squeezes the ones that do. */
  { key: 'payer_name', label: 'Payer', width: '0.7fr' },
  { key: 'outcome', label: 'Outcome', required: true, width: '92px' },
  { key: 'reason', label: 'Reason', required: true, width: '1.5fr' },
  /* The PAYER's number for this claim, not ours (GDN-3383). It is what a
     payer's phone system asks for first, and on a rejected claim its absence
     is itself the answer: nothing reached the payer, so nothing has a payer
     number yet.
     The floor is what the VALUE needs ("No payer claim #"), not what the
     caption wants. It was 150px, which is what "Clearinghouse rejection"
     reads at; that floor plus the disposition column's own 150px floor
     starved the patient name down to 62px. Between a caption that
     ellipsizes and a patient nobody can identify, the caption yields. See
     the column-budget note in the PR: at eleven columns this view is over
     budget and the fix is a product decision, not another few pixels. */
  { key: 'payer_reference', label: 'Payer claim #', width: 'minmax(108px, 0.9fr)' },
  { key: 'amount', label: 'Charge', required: true, type: 'mono', align: 'right', width: '78px' },
  /* Mono so the numbers line up, and so an unanswered claim leaves the cell
     BLANK: the default cell type fills an empty value with a dash. */
  { key: 'days', label: 'Days', align: 'right', type: 'mono', width: '44px' },
];

/** The disposition column is driven by the toggle, not by column
 *  preferences: it exists to explain rows the toggle just revealed. */
export const DISPOSITION_COLUMN: ColumnDef = {
  key: 'disposition',
  label: 'Disposition',
  /* The floor is what "until Sep 10 · M. Lund" needs. Below it the caption
     truncates and drops the one word that says WHO decided, and a chip that
     names nobody invites the biller to override it. */
  width: 'minmax(150px, 1fr)',
};

/**
 * Bill secondary asks a third question (GDN-4023, contract gdn-3991 v1 §7):
 * the primary has been paid, so neither the pre-flight advice nor the
 * payer's verdict is the point. What matters is who the secondary payer is,
 * what the primary left for them, how long the primary's 835 has been
 * sitting, and what stands in the way of sending — the same four facts the
 * Home card shows, each in a column of its own where there is room.
 */
export const getBillSecondaryColumns = (subjectLabel: string): ColumnDef[] => [
  { key: 'service_date', label: 'Date', required: true, width: '82px' },
  { key: 'patient_name', label: subjectLabel, required: true, type: 'patient', width: '1.2fr' },
  /* The cell renderer owns this column, as on Claims to fix: a draft has
     no claim number yet and says so in words. */
  { key: 'claim_number', label: 'Claim #', width: '88px' },
  { key: 'payer_name', label: 'Payer', width: '0.8fr' },
  {
    key: 'secondary_payer',
    label: 'Secondary payer',
    required: true,
    width: 'minmax(120px, 0.9fr)',
  },
  /* `secondary_billable_cents`: what the primary's 835 left. Mono so the
     figures line up, and blank when the row carries none. */
  {
    key: 'secondary_balance',
    label: 'Balance',
    required: true,
    type: 'mono',
    align: 'right',
    width: '84px',
  },
  { key: 'era_age', label: 'Days since ERA', width: '104px' },
  { key: 'secondary_blockers', label: 'Blockers', required: true, width: 'minmax(160px, 1.4fr)' },
  { key: 'work_state', label: 'State', required: true, width: '132px' },
];

/** The default column set for a rail view. Every list and the other three queues share one. */
export const defaultColumnsForView = (railView: WorkViewKey, subjectLabel: string): ColumnDef[] => {
  switch (railView) {
    case 'fix':
      return getFixColumns(subjectLabel);
    case 'bill_secondary':
      return getBillSecondaryColumns(subjectLabel);
    default:
      return getDefaultColumns(subjectLabel);
  }
};
